import { Color } from './constants/colors'
import { LegacyColor } from './constants/colors-legacy'
import { CTATheme } from './constants/themes'

const backwardCompatibility = {
  [Color.Slate]: LegacyColor.Black,
  [Color.Blue]: LegacyColor.Blue,
  [Color.Gray]: LegacyColor.Gray,
  [Color.Sky]: LegacyColor.LightBlue,
  [Color.Stone]: LegacyColor.LightGray,
  [Color.CoralLight]: LegacyColor.LightOrange,
  [Color.VioletLight]: LegacyColor.LightPurple,
  [Color.Ice]: LegacyColor.LightTeal,
  [Color.TangerineLight]: LegacyColor.LightYellow,
  [Color.Coral]: LegacyColor.Orange,
  [Color.Violet]: LegacyColor.Purple,
  [Color.Aqua]: LegacyColor.Teal,
  [Color.UIGray]: LegacyColor.LightGray,
  [Color.UIError]: LegacyColor.UIError,
  [Color.UISuccess]: LegacyColor.UISuccess,
  [Color.UIWarning]: LegacyColor.UIWarning,
  [Color.White]: LegacyColor.White,
  [Color.Tangerine]: LegacyColor.Yellow,
  [Color.Red]: LegacyColor.Red,
  [Color.Transparent]: LegacyColor.Transparent,
  [LegacyColor.Black]: LegacyColor.Black,
  [LegacyColor.Blue]: LegacyColor.Blue,
  [LegacyColor.Gray]: LegacyColor.Gray,
  [LegacyColor.LightBlue]: LegacyColor.LightBlue,
  [LegacyColor.LightGray]: LegacyColor.LightGray,
  [LegacyColor.LightOrange]: LegacyColor.LightOrange,
  [LegacyColor.LightPurple]: LegacyColor.LightPurple,
  [LegacyColor.LightTeal]: LegacyColor.LightTeal,
  [LegacyColor.LightYellow]: LegacyColor.LightYellow,
  [LegacyColor.BlueExtraLight]: LegacyColor.BlueExtraLight,
  [LegacyColor.Orange]: LegacyColor.Orange,
  [LegacyColor.Purple]: LegacyColor.Purple,
  [LegacyColor.Teal]: LegacyColor.Teal,
  [LegacyColor.UIGray]: LegacyColor.UIGray,
  [LegacyColor.UISuccess]: LegacyColor.UISuccess,
  [LegacyColor.UIWarning]: LegacyColor.UIWarning,
  [LegacyColor.White]: LegacyColor.White,
  [LegacyColor.Yellow]: LegacyColor.Yellow,
  [LegacyColor.Red]: LegacyColor.Red,
  [LegacyColor.Transparent]: LegacyColor.Transparent,
}

const forwardCompatibility = {
  [LegacyColor.Black as string]: Color.Slate,
  [LegacyColor.Blue as string]: Color.Blue,
  [LegacyColor.Gray as string]: Color.Gray,
  [LegacyColor.LightBlue as string]: Color.Sky,
  [LegacyColor.LightGray as string]: Color.Stone,
  [LegacyColor.LightOrange as string]: Color.CoralLight,
  [LegacyColor.LightPurple as string]: Color.VioletLight,
  [LegacyColor.LightTeal as string]: Color.Ice,
  [LegacyColor.LightYellow as string]: Color.TangerineLight,
  [LegacyColor.BlueExtraLight as string]: Color.SkyExtraLight,
  [LegacyColor.Orange as string]: Color.Coral,
  [LegacyColor.Purple as string]: Color.Violet,
  [LegacyColor.Teal as string]: Color.Aqua,
  [LegacyColor.UIGray as string]: Color.UIGray,
  [LegacyColor.UISuccess as string]: Color.UISuccess,
  [LegacyColor.UIWarning as string]: Color.UIWarning,
  [LegacyColor.White as string]: Color.White,
  [LegacyColor.Yellow as string]: Color.Tangerine,
  [LegacyColor.Red as string]: Color.Red,
  [LegacyColor.Transparent as string]: Color.Transparent,
  [Color.Slate as string]: Color.Slate,
  [Color.Blue as string]: Color.Blue,
  [Color.Gray as string]: Color.Gray,
  [Color.Sky as string]: Color.Sky,
  [Color.Stone as string]: Color.Stone,
  [Color.CoralLight as string]: Color.CoralLight,
  [Color.VioletLight as string]: Color.VioletLight,
  [Color.Ice as string]: Color.Ice,
  [Color.TangerineLight as string]: Color.TangerineLight,
  [Color.Coral as string]: Color.Coral,
  [Color.Violet as string]: Color.Violet,
  [Color.Aqua as string]: Color.Aqua,
  [Color.UIGray as string]: Color.UIGray,
  [Color.UIError as string]: Color.UIError,
  [Color.UISuccess as string]: Color.UISuccess,
  [Color.UIWarning as string]: Color.UIWarning,
  [Color.White as string]: Color.White,
  [Color.Tangerine as string]: Color.Tangerine,
  [Color.Red as string]: Color.Red,
  [Color.Transparent as string]: Color.Transparent,
}

const defaultLegacyColor = LegacyColor.White
const defaultColor = Color.White

export const getBackwardCompatibility = (color?: string): Color | LegacyColor =>
  backwardCompatibility[color as LegacyColor] || defaultLegacyColor

export const getForwardCompatibility = (color?: string): Color | LegacyColor =>
  forwardCompatibility[color as Color] || defaultColor

export type AllSystemColors = Color | LegacyColor

export type ColorTheme = {
  id: AllSystemColors
  backgroundColor: AllSystemColors
  textColor: AllSystemColors
  CTATheme: CTATheme
  focus?: AllSystemColors
}

export const AllColorValues = {
  Slate: Color.Slate,
  Blue: Color.Blue,
  Gray: Color.Gray,
  Sky: Color.Sky,
  Stone: Color.Stone,
  CoralLight: Color.CoralLight,
  VioletLight: Color.VioletLight,
  Ice: Color.Ice,
  TangerineLight: Color.TangerineLight,
  SkyExtraLight: Color.SkyExtraLight,
  Coral: Color.Coral,
  Violet: Color.Violet,
  Aqua: Color.Aqua,
  UIGray: Color.UIGray,
  UIError: Color.UIError,
  UISuccess: Color.UISuccess,
  UIWarning: Color.UIWarning,
  White: Color.White,
  Tangerine: Color.Tangerine,
  Transparent: Color.Transparent,
  LegacyBlack: LegacyColor.Black,
  LegacyBlue: LegacyColor.Blue,
  LegacyGray: LegacyColor.Gray,
  LegacyLightBlue: LegacyColor.LightBlue,
  LegacyLightGray: LegacyColor.LightGray,
  LegacyLightOrange: LegacyColor.LightOrange,
  LegacyLightPurple: LegacyColor.LightPurple,
  LegacyLightTeal: LegacyColor.LightTeal,
  LegacyLightYellow: LegacyColor.LightYellow,
  LegacyBlueExtraLight: LegacyColor.BlueExtraLight,
  LegacyOrange: LegacyColor.Orange,
  LegacyPurple: LegacyColor.Purple,
  LegacyTeal: LegacyColor.Teal,
  LegacyUIGray: LegacyColor.UIGray,
  LegacyUIError: LegacyColor.UIError,
  LegacyUISuccess: LegacyColor.UISuccess,
  LegacyUIWarning: LegacyColor.UIWarning,
  LegacyWhite: LegacyColor.White,
  LegacyYellow: LegacyColor.Yellow,
  LegacyTransparent: LegacyColor.Transparent,
}

export const ThemesPerBrand = {
  legacy: Array.from(Object.keys(LegacyColor)),
  default: Array.from(Object.keys(Color)),
}

export const AllThemeOptions = [...ThemesPerBrand.default]

export function getThemeForDefaultBrand(themeColor: Color): ColorTheme {
  switch (themeColor) {
    case Color.Sky:
      return {
        id: Color.Sky,
        backgroundColor: Color.Sky,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Sky,
      }

    case Color.Stone:
      return {
        id: Color.Stone,
        backgroundColor: Color.Stone,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Stone,
      }

    case Color.CoralLight:
      return {
        id: Color.CoralLight,
        backgroundColor: Color.CoralLight,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.CoralLight,
      }

    case Color.VioletLight:
      return {
        id: Color.VioletLight,
        backgroundColor: Color.VioletLight,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.VioletLight,
      }

    case Color.Ice:
      return {
        id: Color.Ice,
        backgroundColor: Color.Ice,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Ice,
      }

    case Color.TangerineLight:
      return {
        id: Color.TangerineLight,
        backgroundColor: Color.TangerineLight,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.TangerineLight,
      }

    case Color.Blue:
      return {
        id: Color.Blue,
        backgroundColor: Color.Blue,
        textColor: Color.White,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Blue,
      }

    case Color.Gray:
      return {
        id: Color.Gray,
        backgroundColor: Color.Gray,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Gray,
      }

    case Color.Coral:
      return {
        id: Color.Coral,
        backgroundColor: Color.Coral,
        textColor: Color.White,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Coral,
      }

    case Color.Violet:
      return {
        id: Color.Violet,
        backgroundColor: Color.Violet,
        textColor: Color.White,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Violet,
      }

    case Color.Aqua:
      return {
        id: Color.Aqua,
        backgroundColor: Color.Aqua,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Aqua,
      }

    case Color.Tangerine:
      return {
        id: Color.Tangerine,
        backgroundColor: Color.Tangerine,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Tangerine,
      }

    case Color.Slate:
      return {
        id: Color.Slate,
        backgroundColor: Color.Slate,
        textColor: Color.White,
        CTATheme: CTATheme.TealFill,
        focus: Color.Slate,
      }

    case Color.UIError:
      return {
        id: Color.UIError,
        backgroundColor: Color.UIError,
        textColor: Color.Slate,
        CTATheme: CTATheme.WhiteOutline,
        focus: Color.UIError,
      }

    case Color.UIGray:
      return {
        id: Color.UIGray,
        backgroundColor: Color.UIGray,
        textColor: Color.White,
        CTATheme: CTATheme.WhiteOutline,
        focus: Color.UIGray,
      }

    case Color.UISuccess:
      return {
        id: Color.UISuccess,
        backgroundColor: Color.UISuccess,
        textColor: Color.Slate,
        CTATheme: CTATheme.WhiteOutline,
        focus: Color.UISuccess,
      }

    case Color.UIWarning:
      return {
        id: Color.UIWarning,
        backgroundColor: Color.UIWarning,
        textColor: Color.Slate,
        CTATheme: CTATheme.WhiteOutline,
        focus: Color.UIWarning,
      }

    case Color.White:
      return {
        id: Color.White,
        backgroundColor: Color.White,
        textColor: Color.Slate,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: Color.White,
      }

    case Color.Red:
      return {
        id: Color.Red,
        backgroundColor: Color.Red,
        textColor: Color.Slate,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: Color.White,
      }

    case Color.Transparent:
      return {
        id: Color.Transparent,
        backgroundColor: Color.Transparent,
        textColor: Color.Slate,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: Color.White,
      }

    case Color.SkyExtraLight:
      return {
        id: Color.SkyExtraLight,
        backgroundColor: Color.SkyExtraLight,
        textColor: Color.Slate,
        CTATheme: CTATheme.BlackFill,
        focus: Color.Sky,
      }

    default:
      return {
        id: Color.White,
        backgroundColor: Color.White,
        textColor: Color.Slate,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: Color.White,
      }
  }
}

export function getThemeForLegacyBrand(themeColor: LegacyColor): ColorTheme {
  switch (themeColor) {
    case LegacyColor.LightBlue:
      return {
        id: LegacyColor.LightBlue,
        backgroundColor: LegacyColor.LightBlue,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightBlue,
      }

    case LegacyColor.LightGray:
      return {
        id: LegacyColor.LightGray,
        backgroundColor: LegacyColor.LightGray,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightGray,
      }

    case LegacyColor.LightOrange:
      return {
        id: LegacyColor.LightOrange,
        backgroundColor: LegacyColor.LightOrange,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightOrange,
      }

    case LegacyColor.LightPurple:
      return {
        id: LegacyColor.LightPurple,
        backgroundColor: LegacyColor.LightPurple,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightPurple,
      }

    case LegacyColor.LightTeal:
      return {
        id: LegacyColor.LightTeal,
        backgroundColor: LegacyColor.LightTeal,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightTeal,
      }

    case LegacyColor.LightYellow:
      return {
        id: LegacyColor.LightYellow,
        backgroundColor: LegacyColor.LightYellow,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightYellow,
      }

    case LegacyColor.Blue:
      return {
        id: LegacyColor.Blue,
        backgroundColor: LegacyColor.Blue,
        textColor: LegacyColor.White,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.Blue,
      }

    case LegacyColor.Gray:
      return {
        id: LegacyColor.Gray,
        backgroundColor: LegacyColor.Gray,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.Gray,
      }

    case LegacyColor.Orange:
      return {
        id: LegacyColor.Orange,
        backgroundColor: LegacyColor.Orange,
        textColor: LegacyColor.White,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.Orange,
      }

    case LegacyColor.Purple:
      return {
        id: LegacyColor.Purple,
        backgroundColor: LegacyColor.Purple,
        textColor: LegacyColor.White,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.Purple,
      }

    case LegacyColor.Teal:
      return {
        id: LegacyColor.Teal,
        backgroundColor: LegacyColor.Teal,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.Teal,
      }

    case LegacyColor.Yellow:
      return {
        id: LegacyColor.Yellow,
        backgroundColor: LegacyColor.Yellow,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.Yellow,
      }

    case LegacyColor.Black:
      return {
        id: LegacyColor.Black,
        backgroundColor: LegacyColor.Black,
        textColor: LegacyColor.White,
        CTATheme: CTATheme.TealFill,
        focus: LegacyColor.Black,
      }

    case LegacyColor.UIError:
      return {
        id: LegacyColor.UIError,
        backgroundColor: LegacyColor.UIError,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.WhiteOutline,
        focus: LegacyColor.UIError,
      }

    case LegacyColor.UIGray:
      return {
        id: LegacyColor.UIGray,
        backgroundColor: LegacyColor.UIGray,
        textColor: LegacyColor.White,
        CTATheme: CTATheme.WhiteOutline,
        focus: LegacyColor.UIGray,
      }

    case LegacyColor.UISuccess:
      return {
        id: LegacyColor.UISuccess,
        backgroundColor: LegacyColor.UISuccess,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.WhiteOutline,
        focus: LegacyColor.UISuccess,
      }

    case LegacyColor.UIWarning:
      return {
        id: LegacyColor.UIWarning,
        backgroundColor: LegacyColor.UIWarning,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.WhiteOutline,
        focus: LegacyColor.UIWarning,
      }

    case LegacyColor.White:
      return {
        id: LegacyColor.White,
        backgroundColor: LegacyColor.White,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: LegacyColor.White,
      }

    case LegacyColor.Red:
      return {
        id: LegacyColor.Red,
        backgroundColor: LegacyColor.Red,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: LegacyColor.White,
      }

    case LegacyColor.Transparent:
      return {
        id: LegacyColor.Transparent,
        backgroundColor: LegacyColor.Transparent,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: LegacyColor.White,
      }

    case LegacyColor.BlueExtraLight:
      return {
        id: LegacyColor.BlueExtraLight,
        backgroundColor: LegacyColor.BlueExtraLight,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.BlackFill,
        focus: LegacyColor.LightBlue,
      }

    default:
      return {
        id: LegacyColor.White,
        backgroundColor: LegacyColor.White,
        textColor: LegacyColor.Black,
        CTATheme: CTATheme.LinkOnlyBlack,
        focus: LegacyColor.White,
      }
  }
}

export function getFocusColor(backgroundColor?: AllSystemColors): Color | LegacyColor {
  return backgroundColor === LegacyColor.LightGray ? Color.White : LegacyColor.LightGray
}

export const LegacyColorName = {
  [LegacyColor.Black]: 'black',
  [LegacyColor.Blue]: 'blue',
  [LegacyColor.Gray]: 'gray',
  [LegacyColor.LightBlue]: 'light-blue',
  [LegacyColor.LightGray]: 'light-gray',
  [LegacyColor.LightOrange]: 'light-orange',
  [LegacyColor.LightPurple]: 'light-purple',
  [LegacyColor.LightTeal]: 'light-teal',
  [LegacyColor.LightYellow]: 'light-yellow',
  [LegacyColor.BlueExtraLight]: 'blue-extra-light',
  [LegacyColor.Orange]: 'orange',
  [LegacyColor.Purple]: 'purple',
  [LegacyColor.Teal]: 'teal',
  [LegacyColor.UIError]: 'ui-error',
  [LegacyColor.UIGray]: 'ui-gray',
  [LegacyColor.UISuccess]: 'ui-success',
  [LegacyColor.UIWarning]: 'ui-warning',
  [LegacyColor.White]: 'white',
  [LegacyColor.Yellow]: 'yellow',
  [LegacyColor.Red]: 'red',
  [LegacyColor.Transparent]: 'transparent',
}

export const ColorName = {
  [Color.Slate]: 'slate',
  [Color.Blue]: 'blue',
  [Color.Gray]: 'gray',
  [Color.Sky]: 'sky',
  [Color.Stone]: 'stone',
  [Color.CoralLight]: 'coral-light',
  [Color.VioletLight]: 'violet-light',
  [Color.Ice]: 'ice',
  [Color.TangerineLight]: 'tangerine-light',
  [Color.SkyExtraLight]: 'sky-extra-light',
  [Color.Coral]: 'coral',
  [Color.Violet]: 'violet',
  [Color.Aqua]: 'aqua',
  [Color.UIError]: 'ui-error',
  [Color.UIGray]: 'ui-gray',
  [Color.UISuccess]: 'ui-success',
  [Color.UIWarning]: 'ui-warning',
  [Color.White]: 'white',
  [Color.Tangerine]: 'tangerine',
  [Color.Red]: 'red',
  [Color.Transparent]: 'transparent',
}

// Map legacy color names to new color hex
const getColorFallback = (color: string): Color | undefined => {
  const colorMap: Record<string, Color> = {
    black: Color.Slate,
    blue: Color.Blue,
    'light-blue': Color.Sky,
    'extra-light': Color.Sky,
    'light-gray': Color.Stone,
    orange: Color.CoralLight,
    purple: Color.VioletLight,
    teal: Color.Aqua,
    yellow: Color.TangerineLight,
    white: Color.White,
  }
  return colorMap[color.toLowerCase()] ?? undefined
}

const getLegacyColorFallback = (color: string) => {
  if (color.match(/coral|tan/i)) {
    return LegacyColor.LightGray
  }
  if (color.match(/indigo|violet/i)) {
    return LegacyColor.LightBlue
  }
  if (color.match(/lightest/i)) {
    return LegacyColor.Gray
  }
  return undefined
}

export const mapOldColorToNewColor = (oldHexColor: string): Color =>
  getColorFallback(LegacyColorName[oldHexColor as LegacyColor]) ?? defaultColor

export const getHexColorFromName = (color: string): Color | LegacyColor =>
  AllColorValues[color as keyof typeof AllColorValues] ||
  AllColorValues[`Legacy${color}` as keyof typeof AllColorValues] ||
  getColorFallback(color) ||
  getLegacyColorFallback(color) ||
  defaultColor
